import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Radio, Eye } from "lucide-react";
import { api } from "@/lib/api";
import DOMPurify from "dompurify";
import ShareButton from "@/components/ShareButton";
import LangToggle from "@/components/LangToggle";
import Seo from "@/components/Seo";

export default function NewsDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [views, setViews] = useState(null);
  const [missing, setMissing] = useState(false);
  const [lang, setLang] = useState("en");

  const load = useCallback(() => {
    setItem(null); setRelated([]); setViews(null); setMissing(false);
    api.get(`/news/article/${slug}`).then(r => setItem(r.data)).catch(() => setMissing(true));
    api.get(`/news/article/${slug}/related`).then(r => setRelated(r.data)).catch(() => setRelated([]));
  }, [slug]);

  useEffect(() => {
    if (!item) return;
    const key = `pm_viewed_${slug}`;
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, "1");
      api.post(`/news/article/${slug}/view`).then(r => setViews(r.data.views)).catch(() => {});
    }
  }, [item, slug]);

  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);

  if (missing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-slate-200" data-testid="news-article-missing">
        Article not found.
        <Link to="/news" className="btn-glass">All Updates</Link>
      </div>
    );
  }
  if (!item) return <div className="min-h-screen flex items-center justify-center text-slate-200">Loading…</div>;

  const hasBn = Boolean(item.title_bn);
  const bn = lang === "bn" && hasBn;
  const title = bn ? item.title_bn : item.title;
  const summary = bn ? (item.summary_bn || item.summary) : item.summary;
  const content = bn ? (item.content_bn || item.content) : item.content;

  return (
    <div className="min-h-screen pt-28" data-testid="news-article-page">
      <Seo
        title={item.title}
        description={item.summary}
        path={`/news/${item.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: item.title,
          description: item.summary,
          datePublished: item.published_at,
          image: item.image_url ? [`https://phousebd.com${item.image_url.startsWith("/") ? item.image_url : ""}`] : undefined,
          author: { "@type": "Organization", name: "PromptsHouseBD" },
          publisher: { "@type": "Organization", name: "PromptsHouseBD", logo: { "@type": "ImageObject", url: "https://phousebd.com/logo.png" } },
        }}
      />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <Link to="/news" className="text-xs font-bold uppercase tracking-[0.14em] text-slate-200 hover:text-orange-300 transition-colors" data-testid="news-article-back">
          ← All Updates
        </Link>

        <div className="mt-8 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="chip">{item.category}</span>
            <span className="meta-mono">
              {new Date(item.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </span>
            {item.source && <span className="meta-mono !text-orange-300/80">{item.source}</span>}
            <span className="meta-mono flex items-center gap-1.5" data-testid="news-article-views">
              <Eye size={13} /> {(views ?? item.views ?? 0).toLocaleString()} reads
            </span>
          </div>
          <div className="flex items-center gap-3">
            {hasBn && <LangToggle lang={lang} setLang={setLang} />}
            <ShareButton url={`/api/share/news/${item.slug}`} variant="button" testId={`share-news-article-${item.slug}`} />
          </div>
        </div>

        <h1 className={`mt-6 ${bn ? "font-bn" : "font-display"} font-bold tracking-tight text-3xl sm:text-4xl md:text-5xl text-white leading-[1.12]`} data-testid="news-article-title">
          {title}
        </h1>

        {summary && (
          <p className={`mt-5 text-lg text-slate-100 leading-relaxed border-l-2 border-orange-400/60 pl-4${bn ? " font-bn" : ""}`}>{summary}</p>
        )}

        {item.image_url && (
          <div className="mt-8 rounded-2xl overflow-hidden border border-white/10">
            <img src={item.image_url} alt="" className="w-full object-cover" data-testid="news-article-image" />
          </div>
        )}

        <div
          className={`rich-content mt-8${bn ? " font-bn" : ""}`}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, { ADD_ATTR: ["target"] }) }}
          data-testid="news-article-content"
        />

        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className="mt-10 card-void p-5 flex items-center justify-between gap-4 group"
            data-testid="news-article-source"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-400/30 flex items-center justify-center shrink-0">
                <Radio className="text-orange-400" size={17} />
              </span>
              <div className="min-w-0">
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-200">Source</div>
                <div className="text-sm text-orange-300 truncate group-hover:text-orange-200 transition-colors">{item.url}</div>
              </div>
            </div>
            <ArrowUpRight size={18} className="text-slate-200 group-hover:text-orange-400 transition-colors shrink-0" />
          </a>
        )}

        {related.length > 0 && (
          <section className="mt-16 pt-10 border-t border-white/10" data-testid="related-articles">
            <div className="flex items-center justify-between gap-4 mb-6">
              <h2 className="font-display font-bold text-xl md:text-2xl text-white tracking-tight">Keep exploring</h2>
              <Link to="/news" className="text-xs font-bold uppercase tracking-[0.14em] text-slate-200 hover:text-orange-300 transition-colors" data-testid="related-view-all">
                All updates →
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r) => {
                const internal = r.is_article && r.slug;
                const CardInner = (
                  <>
                    {r.image_url && (
                      <div className="aspect-[16/9] overflow-hidden">
                        <img src={r.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                    )}
                    <div className="p-4 flex flex-col gap-2 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="chip">{r.category}</span>
                        <span className="meta-mono">
                          {new Date(r.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      </div>
                      <h3 className="font-display font-semibold text-sm md:text-base text-white leading-snug group-hover:text-orange-200 transition-colors line-clamp-2">
                        {r.title}
                      </h3>
                    </div>
                  </>
                );
                return internal ? (
                  <Link key={r.id} to={`/news/${r.slug}`} className="card-void overflow-hidden group flex flex-col" data-testid={`related-article-${r.slug}`}>
                    {CardInner}
                  </Link>
                ) : (
                  <a key={r.id} href={r.url} target="_blank" rel="noreferrer" className="card-void overflow-hidden group flex flex-col" data-testid={`related-article-${r.id}`}>
                    {CardInner}
                  </a>
                );
              })}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}
